import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { sl } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { contactFormContent } from "@/content/homepage";
import { cn } from "@/lib/utils";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useServices } from "@/hooks/useServices";
import { useOccupiedTimeSlots } from "@/hooks/useReminders";

// Validation schema for contact form
const contactSchema = z.object({
  name: z.string().trim().min(1, "Ime je obvezno").max(100, "Ime je predolgo (max 100 znakov)"),
  email: z.string().trim().email("Neveljaven email naslov").max(255, "Email je predolg (max 255 znakov)"),
  phone: z.string().trim().min(5, "Telefon mora imeti vsaj 5 znakov").max(30, "Telefon je predolg (max 30 znakov)"),
  company: z.string().trim().min(1, "Ime podjetja je obvezno").max(200, "Ime podjetja je predolgo (max 200 znakov)"),
  website: z.string().trim().max(500, "URL je predolg (max 500 znakov)").regex(/^https?:\/\//i, "URL se mora začeti z http:// ali https://").optional().or(z.literal("")),
  message: z.string().trim().max(2000, "Sporočilo je predolgo (max 2000 znakov)").optional().or(z.literal("")),
  services: z.array(z.string()).min(1, "Izbrati morate vsaj eno storitev").max(10, "Preveč izbranih storitev"),
});

interface ContactFormProps {
  isOpen: boolean;
  onClose: () => void;
  isWaitlist: boolean;
  selectedPackage?: string | null;
  source?: string;
}

export const ContactForm = ({ isOpen, onClose, isWaitlist, selectedPackage, source }: ContactFormProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    website: "",
    message: "",
    services: [] as string[],
    consultationDate: undefined as Date | undefined,
    consultationTime: "" as string
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unavailableDates, setUnavailableDates] = useState<Date[]>([]);
  const [bookingWindowDays, setBookingWindowDays] = useState(30);
  const [disableWeekends, setDisableWeekends] = useState(false);

  // Get occupied time slots from reminders
  const { isSlotOccupied, fetchOccupiedSlots } = useOccupiedTimeSlots();

  // Fetch unavailable dates and form settings
  useEffect(() => {
    const fetchFormSettings = async () => {
      const [datesResult, settingsResult] = await Promise.all([
        supabase.from("cms_unavailable_dates").select("date"),
        supabase.from("cms_form_settings").select("*").single(),
      ]);
      
      if (datesResult.data) {
        setUnavailableDates(datesResult.data.map((d) => new Date(d.date)));
      }
      
      if (settingsResult.data) {
        setBookingWindowDays(settingsResult.data.booking_window_days);
        setDisableWeekends(settingsResult.data.disable_weekends);
      }
    };
    
    if (isOpen) {
      fetchFormSettings();
      fetchOccupiedSlots();
    }
  }, [isOpen]);

  const { services: serviceOptions } = useServices();

  // Get available hours for selected date
  const availableHours = useMemo(() => {
    if (!formData.consultationDate) return [];
    
    const hours = Array.from({ length: 9 }, (_, i) => i + 9); // 9:00 - 17:00
    return hours.filter((hour) => !isSlotOccupied(formData.consultationDate!, hour));
  }, [formData.consultationDate, isSlotOccupied]);

  const handleServiceToggle = (serviceId: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(s => s !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate form data
      const validationResult = contactSchema.safeParse({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        website: formData.website,
        message: formData.message,
        services: formData.services,
      });

      if (!validationResult.success) {
        const firstError = validationResult.error.errors[0];
        toast({
          variant: "destructive",
          title: "Napaka pri validaciji",
          description: firstError.message,
        });
        setIsSubmitting(false);
        return;
      }

      const validatedData = validationResult.data;

      // Combine date and time for consultation datetime
      let consultationDatetime: string | null = null;
      if (formData.consultationDate && formData.consultationTime) {
        const [hours, minutes] = formData.consultationTime.split(':').map(Number);
        const datetime = new Date(formData.consultationDate);
        datetime.setHours(hours, minutes, 0, 0);
        consultationDatetime = datetime.toISOString();
      }

      // Save to database
      const { error: dbError } = await supabase
        .from('contact_submissions')
        .insert({
          name: validatedData.name,
          email: validatedData.email,
          phone: validatedData.phone,
          company: validatedData.company,
          website: validatedData.website || null,
          message: validatedData.message || null,
          selected_package: selectedPackage || null,
          source: source || null,
          is_waitlist: isWaitlist,
          services: validatedData.services,
          consultation_datetime: consultationDatetime,
        });

      if (dbError) {
        console.error('Database error:', dbError);
        (window as any).posthog?.capture?.('contact_form_submit_failed', {
          stage: 'db_insert',
          error_code: (dbError as any).code,
          error_message: dbError.message,
          is_waitlist: isWaitlist,
          selected_package: selectedPackage || null,
          source: source || null,
          services: validatedData.services,
          has_consultation: !!consultationDatetime,
        });
        throw new Error('Napaka pri shranjevanju podatkov');
      }



      // Send emails
      const { error: emailError } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: validatedData.name,
          email: validatedData.email,
          phone: validatedData.phone,
          company: validatedData.company,
          website: validatedData.website,
          message: validatedData.message,
          selectedPackage: selectedPackage,
          source: source,
          services: validatedData.services,
        },
      });

      if (emailError) {
        console.error('Email error:', emailError);
        // Check if it's a rate limit error
        if (emailError.message?.includes("Rate limit")) {
          throw new Error("Presegli ste omejitev zahtevkov. Prosimo poskusite čez nekaj časa.");
        }
        // Don't throw error for other email errors - form was submitted successfully to DB
      }

      const successMsg = isWaitlist 
        ? contactFormContent.successMessage.waitlist 
        : contactFormContent.successMessage.withPricing;
      
      toast({
        title: successMsg.title,
        description: successMsg.description,
      });

      setFormData({ name: "", email: "", phone: "", company: "", website: "", message: "", services: [], consultationDate: undefined, consultationTime: "" });
      onClose();
      navigate("/hvala");
    } catch (error) {
      console.error('Form submission error:', error);
      (window as any).posthog?.capture?.('contact_form_submit_failed', {
        stage: 'exception',
        error_message: error instanceof Error ? error.message : String(error),
        is_waitlist: isWaitlist,
        selected_package: selectedPackage || null,
        source: source || null,
      });
      toast({
        title: "Napaka",
        description: error instanceof Error ? error.message : "Prišlo je do napake pri oddaji obrazca. Prosimo poskusite znova.",
        variant: "destructive",
      });

    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader className="space-y-3">
          <DialogTitle>
            {isWaitlist ? contactFormContent.title.waitlist : contactFormContent.title.withPricing}
          </DialogTitle>
          <DialogDescription>
            {isWaitlist ? contactFormContent.description.waitlist : contactFormContent.description.withPricing}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-5 mt-2">
          <div>
            <Label htmlFor="name">{contactFormContent.fields.name.label}</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={contactFormContent.fields.name.placeholder}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="email">{contactFormContent.fields.email.label}</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder={contactFormContent.fields.email.placeholder}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="phone">{contactFormContent.fields.phone.label}</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder={contactFormContent.fields.phone.placeholder}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="company">{contactFormContent.fields.company.label}</Label>
            <Input
              id="company"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              placeholder={contactFormContent.fields.company.placeholder}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="website">{contactFormContent.fields.website.label}</Label>
            <Input
              id="website"
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder={contactFormContent.fields.website.placeholder}
            />
          </div>
          
          <div>
            <Label className="text-base">
              Potrebujem naslednje storitve <span className="text-destructive">*</span>
            </Label>
            <div className="mt-3 space-y-3">
              {serviceOptions.map((service) => (
                <div key={service.id} className="flex items-center space-x-3">
                  <Checkbox
                    id={service.id}
                    checked={formData.services.includes(service.id)}
                    onCheckedChange={() => handleServiceToggle(service.id)}
                  />
                  <label
                    htmlFor={service.id}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {service.label}
                  </label>
                </div>
              ))}
            </div>
            {formData.services.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                Prosimo izberite vsaj eno storitev
              </p>
            )}
          </div>

          <div>
            <Label>Želeni datum in ura posveta</Label>
            <div className="flex gap-2 mt-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "flex-1 justify-start text-left font-normal",
                      !formData.consultationDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.consultationDate ? (
                      format(formData.consultationDate, "d. MMMM yyyy", { locale: sl })
                    ) : (
                      <span>Izberi datum</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.consultationDate}
                    onSelect={(date) => setFormData({ ...formData, consultationDate: date, consultationTime: "" })}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      // Past dates
                      if (date < today) return true;
                      // Beyond booking window
                      const maxDate = new Date(today);
                      maxDate.setDate(maxDate.getDate() + bookingWindowDays);
                      if (date > maxDate) return true;
                      // Weekends if disabled
                      if (disableWeekends) {
                        const dayOfWeek = date.getDay();
                        if (dayOfWeek === 0 || dayOfWeek === 6) return true;
                      }
                      // Manually set unavailable dates
                      return unavailableDates.some(
                        (d) => d.toDateString() === date.toDateString()
                      );
                    }}
                    initialFocus
                    weekStartsOn={1}
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
              <Select
                value={formData.consultationTime}
                onValueChange={(value) => setFormData({ ...formData, consultationTime: value })}
                disabled={!formData.consultationDate || availableHours.length === 0}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Ura" />
                </SelectTrigger>
                <SelectContent>
                  {availableHours.length > 0 ? (
                    availableHours.map((hour) => (
                      <SelectItem key={hour} value={`${hour}:00`}>
                        {hour}:00
                      </SelectItem>
                    ))
                  ) : (
                    <div className="py-2 px-3 text-sm text-muted-foreground">
                      {formData.consultationDate ? "Ni prostih terminov" : "Najprej izberite datum"}
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
            {formData.consultationDate && availableHours.length === 0 && (
              <p className="text-sm text-muted-foreground mt-1">
                Ta dan nima prostih terminov. Prosimo izberite drug datum.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="message">{contactFormContent.fields.message.label}</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder={contactFormContent.fields.message.placeholder}
              rows={3}
            />
          </div>
          
          <Button
            type="submit" 
            disabled={isSubmitting}
            className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 h-12 text-base rounded-full shadow-lg hover:shadow-xl transition-all mt-6"
          >
            {isSubmitting ? "Pošiljam..." : (isWaitlist ? contactFormContent.submitButton.waitlist : contactFormContent.submitButton.withPricing)}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
